import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import { useAuthToken } from "@/auth/useAuthToken";

export type ApiDataState<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: T };

/**
 * Fetch-on-mount with an Auth0 bearer token and a cancellation guard
 * (same pattern as gft-shopper-app). `fetcher` must be referentially
 * stable — pass the module-level API client function.
 *
 * A 401 gets one forced token refresh + retry before it becomes an error —
 * the token can expire between acquisition and the API's validation. A
 * second 401 falls through to the error card rather than looping.
 */
export function useApiData<T>(
  fetcher: (token: string) => Promise<T>,
): ApiDataState<T> & { reload: () => void } {
  const getToken = useAuthToken();
  const [state, setState] = useState<ApiDataState<T>>({ status: "loading" });
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    (async () => {
      try {
        let data: T;
        try {
          data = await fetcher(await getToken());
        } catch (err) {
          if (!(err instanceof ApiError) || err.status !== 401 || cancelled) {
            throw err;
          }
          data = await fetcher(await getToken({ forceRefresh: true }));
        }
        if (!cancelled) setState({ status: "ready", data });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Request failed",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetcher, getToken, reloadCount]);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  return { ...state, reload };
}
